import { useEffect, useState, useCallback, useRef } from "react";
import api, { API_BASE } from "../lib/api";
import { fmtDate, ESTADO_LABEL, ESTADO_COLOR } from "../lib/utils";
import {
  Button, Select, Badge, Spinner, Empty, PageHeader,
  Card, CardContent, Sheet,
} from "../components/ui";
import { LayoutGrid, List, Camera, Clock, CheckSquare, Printer } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import ConformidadCliente from "../components/ConformidadCliente";

const TIPO_ITEM = {
  MANO_OBRA: "Mano de obra",
  REPUESTO:  "Repuesto",
  PINTURA:   "Pintura",
  OTROS:     "Otros",
};

const KANBAN_COLS = [
  { key: "PENDIENTE",       label: "Pendiente",        color: "bg-slate-100 dark:bg-slate-800" },
  { key: "EN_PROCESO",      label: "En proceso",       color: "bg-blue-50 dark:bg-blue-950/40" },
  { key: "CONTROL_CALIDAD", label: "Control calidad",  color: "bg-amber-50 dark:bg-amber-950/40" },
  { key: "LISTO",           label: "Listo",            color: "bg-green-50 dark:bg-green-950/40" },
  { key: "ENTREGADO",       label: "Entregado",        color: "bg-muted" },
];

export default function Ordenes() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [view,    setView]    = useState("kanban");
  const [detail,  setDetail]  = useState(null);
  const [tecF,    setTecF]    = useState("");
  const [tecnicos,setTec]     = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [conformidad, setConformidad] = useState(null);  // {data, autoPrint}

  const load = useCallback(async () => {
    const params = {};
    if (tecF) params.tecnico_id = tecF;
    const [o, t] = await Promise.all([
      api.get("/ordenes", { params }),
      api.get("/tecnicos"),
    ]);
    setList(o.data);
    setTec(t.data);
    setLoading(false);
  }, [tecF]);

  useEffect(() => { load(); }, [load]);

  async function cambiarEstado(id, estado) {
    try {
      await api.patch(`/ordenes/${id}/estado`, null, { params: { estado } });
      toast.success(`Estado → ${ESTADO_LABEL[estado] ?? estado}`);
      load();
      setRefreshKey((k) => k + 1);
      if (estado === "ENTREGADO") {
        try {
          const r = await api.get(`/ordenes/${id}/conformidad`);
          setConformidad({ data: r.data, autoPrint: true });
        } catch {
          toast.error("No se pudo cargar la conformidad para imprimir");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Error al cambiar estado");
    }
  }

  async function imprimirConformidad(id) {
    try {
      const r = await api.get(`/ordenes/${id}/conformidad`);
      setConformidad({ data: r.data, autoPrint: false });
    } catch {
      toast.error("No se pudo cargar la conformidad");
    }
  }

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  const byEstado = (k) => list.filter((o) => o.estado === k);

  return (
    <div className="p-4 sm:p-6 flex flex-col h-full">
      <PageHeader title="Órdenes de Trabajo" sub={`${list.length} órdenes`}>
        <Select value={tecF} onChange={(e) => setTecF(e.target.value)} className="w-full sm:w-44">
          <option value="">Todos los técnicos</option>
          {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </Select>
        <div className="flex rounded-md border border-border overflow-hidden">
          <button
            onClick={() => setView("kanban")}
            className={cn("px-2.5 py-1.5 text-xs transition-colors", view === "kanban" ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView("list")}
            className={cn("px-2.5 py-1.5 text-xs transition-colors", view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </PageHeader>

      {view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto flex-1 pb-2 -mx-4 sm:mx-0 px-4 sm:px-0">
          {KANBAN_COLS.map((col) => {
            const cards = byEstado(col.key);
            return (
              <div key={col.key} className={cn("flex flex-col rounded-lg min-w-[200px] w-[220px] sm:w-[260px] shrink-0", col.color)}>
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-sm font-semibold">{col.label}</span>
                  <span className="text-xs text-muted-foreground bg-background rounded-full px-2 py-0.5">{cards.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {cards.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">Sin órdenes</p>
                  )}
                  {cards.map((ot) => (
                    <KanbanCard key={ot.id} ot={ot} onOpen={() => setDetail(ot)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <OrdenesList list={list} onOpen={setDetail} />
      )}

      {/* Sheet detalle */}
      <Sheet open={!!detail} onClose={() => setDetail(null)} title={`OT ${detail?.numero ?? ""}`}>
        {detail && (
          <OrdenDetail
            id={detail.id}
            onEstado={cambiarEstado}
            onImprimirConformidad={imprimirConformidad}
            refreshKey={refreshKey}
          />
        )}
      </Sheet>

      {/* Conformidad del cliente */}
      {conformidad && (
        <ConformidadCliente
          data={conformidad.data}
          autoPrint={conformidad.autoPrint}
          onClose={() => setConformidad(null)}
        />
      )}
    </div>
  );
}

function KanbanCard({ ot, onOpen }) {
  const overdue = ot.fecha_estimada && new Date(ot.fecha_estimada) < new Date();
  return (
    <button
      onClick={onOpen}
      className={cn(
        "w-full text-left rounded-md border border-border bg-card p-3 shadow-sm",
        "hover:border-primary/50 transition-colors space-y-2"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-tight">{ot.vehiculo_desc}</p>
        {overdue && <span className="text-accent shrink-0 text-xs">⚠</span>}
      </div>
      <div className="space-y-0.5">
        <p className="text-xs text-muted-foreground font-mono">{ot.numero}</p>
        {ot.presupuesto_numero && (
          <p className="text-xs text-muted-foreground font-mono">{ot.presupuesto_numero}</p>
        )}
        <p className="text-xs font-mono"
          style={{ color: ot.aseguradora_id ? undefined : "var(--muted-foreground)" }}>
          {ot.numero_provision
            ? <span className="text-foreground">Prov. {ot.numero_provision}</span>
            : <span>{ot.aseguradora_id ? "Sin N° prov." : "PARTICULAR"}</span>}
        </p>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{ot.tecnico_nombre ?? "Sin técnico"}</span>
        {ot.fecha_estimada && (
          <span className={cn("flex items-center gap-1", overdue && "text-accent font-medium")}>
            <Clock className="w-3 h-3" />
            {fmtDate(ot.fecha_estimada)}
          </span>
        )}
      </div>
      {ot.progreso_pct > 0 && (
        <div className="w-full rounded-full bg-muted h-1.5 overflow-hidden">
          <div className="h-full bg-primary rounded-full" style={{ width: `${ot.progreso_pct}%` }} />
        </div>
      )}
    </button>
  );
}

function OrdenesList({ list, onOpen }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border">
            {["N°","Vehículo","Técnico","Inicio","Estimada","Progreso","Estado",""].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr><td colSpan={8}><Empty message="No hay órdenes" /></td></tr>
          )}
          {list.map((ot) => (
            <tr key={ot.id} className="border-b border-border hover:bg-secondary/30">
              <td className="px-3 py-2 font-mono text-xs">{ot.numero}</td>
              <td className="px-3 py-2">
                <p className="font-medium">{ot.vehiculo_desc}</p>
                <p className="text-xs text-muted-foreground">{ot.cliente_nombre}</p>
              </td>
              <td className="px-3 py-2">{ot.tecnico_nombre ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{fmtDate(ot.fecha_inicio)}</td>
              <td className="px-3 py-2 text-xs">{ot.fecha_estimada ? fmtDate(ot.fecha_estimada) : "—"}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${ot.progreso_pct}%` }} />
                  </div>
                  <span className="text-xs font-mono">{ot.progreso_pct}%</span>
                </div>
              </td>
              <td className="px-3 py-2">
                <Badge className={ESTADO_COLOR[ot.estado]}>{ESTADO_LABEL[ot.estado] ?? ot.estado}</Badge>
              </td>
              <td className="px-3 py-2">
                <button onClick={() => onOpen(ot)} className="text-xs text-primary hover:underline">Ver</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdenDetail({ id, onEstado, onImprimirConformidad, refreshKey }) {
  const [ot,       setOt]       = useState(null);
  const [fotos,    setFotos]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [uploading,setUpl]      = useState(false);
  const [progrVal, setProgr]    = useState(0);
  const fileRef = useRef(null);



  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/ordenes/${id}`),
      api.get("/fotos", { params: { entidad: "orden", entidad_id: id } }),
    ]).then(([o, f]) => {
      setOt(o.data);
      setFotos(f.data);
      setProgr(o.data.progreso_pct ?? 0);
      setLoading(false);
    });
  }, [id, refreshKey]);

  async function saveProgreso() {
    await api.put(`/ordenes/${id}`, { progreso_pct: progrVal });
    toast.success("Progreso actualizado");
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpl(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entidad", "orden");
    fd.append("entidad_id", id);
    try {
      const r = await api.post("/fotos", fd);
      setFotos((f) => [...f, r.data]);
      toast.success("Foto subida");
    } catch (err) { toast.error(err?.response?.data?.detail ?? "Error al subir foto"); }
    finally {
      setUpl(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const ESTADOS_NEXT = {
    PENDIENTE:       ["EN_PROCESO"],
    EN_PROCESO:      ["CONTROL_CALIDAD","PENDIENTE"],
    CONTROL_CALIDAD: ["LISTO","EN_PROCESO"],
    LISTO:           ["ENTREGADO","CONTROL_CALIDAD"],
    ENTREGADO:       [],
  };

  const next = ESTADOS_NEXT[ot.estado] ?? [];

  return (
    <div className="space-y-5">
      {/* Info */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-muted-foreground">Vehículo: </span>{ot.vehiculo_desc}</div>
        <div><span className="text-muted-foreground">Técnico: </span>{ot.tecnico_nombre ?? "—"}</div>
        <div><span className="text-muted-foreground">Estado: </span>
          <Badge className={ESTADO_COLOR[ot.estado]}>{ESTADO_LABEL[ot.estado] ?? ot.estado}</Badge>
        </div>
        <div><span className="text-muted-foreground">Estimada: </span>{ot.fecha_estimada ? fmtDate(ot.fecha_estimada) : "—"}</div>
        {ot.presupuesto_numero && (
          <div><span className="text-muted-foreground">Presupuesto: </span>
            <span className="font-mono">{ot.presupuesto_numero}</span>
          </div>
        )}
        <div><span className="text-muted-foreground">Seguro: </span>
          {ot.numero_provision
            ? <span className="font-mono">Prov. {ot.numero_provision}</span>
            : ot.aseguradora_id
              ? <span className="text-muted-foreground">{ot.aseguradora_nombre} — Sin N° prov.</span>
              : <span className="font-semibold">PARTICULAR</span>
          }
        </div>
      </div>

      {/* Progreso */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Progreso</span>
          <span className="font-mono text-sm">{progrVal}%</span>
        </div>
        <input
          type="range" min={0} max={100} step={5}
          value={progrVal}
          onChange={(e) => setProgr(parseInt(e.target.value))}
          className="w-full accent-primary"
        />
        <Button size="sm" variant="outline" onClick={saveProgreso}>Guardar progreso</Button>
      </div>

      {/* Cambiar estado */}
      {next.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {next.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={s === "ENTREGADO" ? "default" : "outline"}
              onClick={() => onEstado(id, s)}
            >
              → {ESTADO_LABEL[s] ?? s}
            </Button>
          ))}
        </div>
      )}

      {/* Reimprimir conformidad (sólo cuando está ENTREGADO) */}
      {ot.estado === "ENTREGADO" && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onImprimirConformidad(id)}
        >
          <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir Conformidad
        </Button>
      )}

      {/* Log de eventos */}
      {(() => {
        const logEvts = Array.isArray(ot.log_eventos) ? ot.log_eventos : [];
        return logEvts.length > 0 ? (
          <div>
            <p className="text-sm font-medium mb-2">Historial</p>
            <div className="space-y-1">
              {[...logEvts].reverse().map((ev, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <span className="shrink-0">{fmtDate(ev.fecha)}</span>
                  <span>{ev.anterior ?? "—"} → <strong>{ev.estado ?? "—"}</strong></span>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* Trabajos y Repuestos del presupuesto */}
      {ot.items && ot.items.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">Trabajos y Repuestos</p>
          <div className="space-y-1">
            {ot.items.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm py-1 border-b border-border last:border-0">
                <CheckSquare className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <span>{item.descripcion}</span>
                  {item.cantidad !== 1 && (
                    <span className="text-muted-foreground ml-1">× {item.cantidad}</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {TIPO_ITEM[item.tipo] ?? item.tipo}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notas */}
      {ot.notas && (
        <div>
          <p className="text-sm font-medium mb-1">Notas del técnico</p>
          <p className="text-sm text-muted-foreground">{ot.notas}</p>
        </div>
      )}

      {/* Fotos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium">Fotos</p>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
              <Camera className="w-3.5 h-3.5 mr-1" /> {uploading ? "Subiendo..." : "Subir"}
            </Button>
          </div>
        </div>
        {fotos.length === 0 ? (
          <Empty message="Sin fotos" icon={Camera} />
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {fotos.map((f) => (
              <div key={f.id} className="aspect-square rounded-md overflow-hidden border border-border">
                <img src={`${API_BASE}${f.url}`} alt="foto" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
